<script lang="ts" setup>
import { TechDataVehicle, Translations, TransmissionMode } from '@/types/types';
import TechDataModal from '@/components/tech-data-modal.vue';
import { computed, ref } from 'vue';
import { getDefaultLocaleValue } from '../locales/default';
import { useTracking } from '../composables/useTracking';
import { vHasScroll } from '../directives/v-has-scroll';

type Weights = Partial<
  Record<TransmissionMode, { weightMax?: { value?: number } }>
>;

const props = defineProps<{
  translations: Translations;
  vehicle: TechDataVehicle;
  vehicles: TechDataVehicle[];
  locale: string;
  modelSeries: string;
  validOptions: {
    weights: number[] | null;
    roofs: string[] | null;
    cabins: string[] | null;
    drivetrains: string[] | null;
    batteryCapacities: number[] | null;
    engines: string[] | null;
  };
  transmissionMode: TransmissionMode | null;
}>();

const isOpen = ref(false);

// Tracking
const { trackModalButton } = useTracking(props.modelSeries);

// Track modal open button
function handleOpenModal() {
  trackModalButton('all_technical_data', props.vehicle);
  isOpen.value = true;
}

const t = (key: string, fallbackKey: string) =>
  props.translations[key] ?? getDefaultLocaleValue(fallbackKey);

const formatWithUnit = (value?: number | null, unit?: string | null) => {
  if (value == null) return '';
  const formatted = value.toLocaleString(props.locale);
  return unit ? `${formatted} ${unit}` : formatted;
};

const mode = computed(() => props.transmissionMode?.toLocaleLowerCase());

const TRIM_RANK: Record<string, number> = {
  basic: 0,
  base: 1,
  pro: 2,
  select: 3,
};

function trimRank(trim: unknown) {
  const key = String(trim ?? '').toLowerCase();
  return TRIM_RANK[key] ?? 999;
}

const filteredVehicles = computed(() => {
  const m = mode.value as keyof Weights | undefined;
  const baseFiltered = props.vehicles.filter((v) => {
    const engineMatch = v.engineType === props.vehicle.engineType;
    const drivetrainMatch = v.drivetrainType === props.vehicle.drivetrainType;
    const batteryMatch = v.batteryCapacity === props.vehicle.batteryCapacity;
    const weightMatch =
      m == null ||
      v.weights?.[m]?.weightMax?.value ===
        props.vehicle.weights?.[m]?.weightMax?.value;
    const cabinMatch = v.cabinType === props.vehicle.cabinType;
    const roofMatch = v.roofHeightType === props.vehicle.roofHeightType;

    return (
      engineMatch &&
      drivetrainMatch &&
      batteryMatch &&
      weightMatch &&
      cabinMatch &&
      roofMatch
    );
  });

  const byEngine = new Map<string, any>();

  for (const v of baseFiltered) {
    const key = v.engine?.engineName ?? v.engineType ?? '';
    const current = byEngine.get(key);

    if (!current || trimRank(v.trim) < trimRank(current.trim)) {
      byEngine.set(key, v);
    }
  }

  return Array.from(byEngine.values()).sort((a, b) => {
    const aName = a.engine?.engineName ?? '';
    const bName = b.engine?.engineName ?? '';
    return aName.localeCompare(bName);
  });
});
</script>

<template>
  <div class="owvans-tech-data-overview">
    <div class="owvans-tech-data-overview__table-container" v-has-scroll>
      <table
        class="wbx-table wbx-table--striped owvans-tech-data-overview__table"
      >
        <thead>
          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="col">
              <span class="sr-only">
                {{
                  props.translations['owvc_tech_data_technical_data'] ||
                  getDefaultLocaleValue('technicalData')
                }}
              </span>
            </th>
            <th
              v-for="(vehicle, index) in filteredVehicles"
              :key="index"
              scope="col"
            >
              <span>{{ vehicle.engine?.engineName || '—' }}</span>
            </th>
          </tr>
        </thead>

        <tbody class="wbx-body">
          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>{{
                props.translations['owvc_tech_data_performance'] ||
                getDefaultLocaleValue('performance')
              }}</span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span v-if="vehicle.engine?.kwPower && vehicle.engine?.hpPower">
                {{ vehicle.engine.kwPower.value }} kW ({{
                  vehicle.engine.hpPower.value
                }}
                PS)
              </span>

              <!-- Fallback for empty vehicle objects -->
              <span v-else>—</span>
            </td>
          </tr>

          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>{{
                props.translations['owvc_tech_data_emissions_co2_combined'] ||
                getDefaultLocaleValue('emissions_co2_combined')
              }}</span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span
                >{{
                  mode
                    ? (vehicle.emissionsConsumption?.[mode]?.emissions
                        ?.individual ?? '-')
                    : '-'
                }}
                g/km
              </span>
            </td>
          </tr>

          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>
                {{
                  t(
                    'owvc_tech_data_consumption_combined',
                    'consumption_combined',
                  )
                }}
              </span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span>
                {{
                  (() => {
                    const m = mode ?? 'manual';
                    const data = vehicle.emissionsConsumption?.[m]?.consumption;
                    return data ? `${data.individual} ${data.unit}` : '—';
                  })()
                }}
              </span>
            </td>
          </tr>

          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>
                {{ t('owvc_tech_data_body_seats', 'body_seats') }}
              </span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span>
                {{ `${vehicle.body?.seats?.value}` }}
              </span>
            </td>
          </tr>

          <tr v-if="vehicle.loadingSpaceDimensions?.lengthMax?.value">
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>
                {{
                  t(
                    'owvc_tech_data_loadingSpaceDimensions_lengthMax',
                    'loadingSpaceDimensions_lengthMax',
                  )
                }}
              </span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span>
                {{
                  formatWithUnit(
                    vehicle.loadingSpaceDimensions?.lengthMax?.value,
                    vehicle.loadingSpaceDimensions?.lengthMax?.unit,
                  )
                }}
              </span>
            </td>
          </tr>

          <tr v-if="vehicle.loadingSpaceDimensions?.heightMax?.value">
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>
                {{
                  t(
                    'owvc_tech_data_loadingSpaceDimensions_heightMax',
                    'loadingSpaceDimensions_heightMax',
                  )
                }}
              </span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span>
                {{
                  formatWithUnit(
                    vehicle.loadingSpaceDimensions?.heightMax?.value,
                    vehicle.loadingSpaceDimensions?.heightMax?.unit,
                  )
                }}
              </span>
            </td>
          </tr>

          <tr>
            <th class="owvans-tech-data-overview__sticky-cell" scope="row">
              <span>
                {{
                  t('owvc_tech_data_weights_payloadMax', 'weights_payloadMax')
                }}
              </span>
            </th>
            <td v-for="(vehicle, index) in filteredVehicles" :key="index">
              <span>
                {{
                  (() => {
                    const m = mode ?? 'manual';
                    const w = vehicle.weights?.[m]?.payloadMax;

                    return w && w.value != null
                      ? formatWithUnit(w.value, w.unit)
                      : '—';
                  })()
                }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="wbx-button-wrapper owvans-tech-data-overview__actions">
      <button
        class="wbx-button wbx-button--secondary wbx-button--large"
        data-test-id="owvans-tech-data-modal-open-button"
        style="flex: 0 1 auto"
        @click="handleOpenModal"
      >
        {{ t('owvc_tech_data_all_technical_data', 'allTechnicalData') }}
      </button>
    </div>

    <tech-data-modal
      :translations="translations"
      :locale="locale"
      :vehicle="vehicle"
      :vehicles="filteredVehicles"
      :validOptions="validOptions"
      :isOpen="isOpen"
      :modelSeries="modelSeries"
      :transmissionMode="transmissionMode"
      @close="isOpen = false"
    />

    <div class="owvans-tech-data-overview__footnote">
      <span class="wb-text-s">
        {{
          translations['owvc_tech_data_disclaimer'] ||
          getDefaultLocaleValue('disclaimer')
        }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.owvans-tech-data-overview {
  --inline-margin: calc((100vw - var(--wb-grid-width)) / 2);
  $block: &;

  &__table-container {
    display: block;

    margin-bottom: var(--wb-spacing-static-medium);
    margin-inline-start: var(--inline-margin);

    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: none;
    scrollbar-width: thin;

    position: relative;
    isolation: isolate;
  }

  &__table {
    width: calc(100% - var(--inline-margin));

    margin-top: var(--wb-spacing-static-medium);
    margin-inline-end: var(--inline-margin);

    overflow: visible;
    border-collapse: separate;
    border-spacing: 0;

    th {
      padding-top: var(--wb-spacing-static-small);
      padding-bottom: var(--wb-spacing-static-small);
    }

    td {
      white-space: nowrap;
    }

    th,
    td {
      text-align: end;
    }

    th#{$block}__sticky-cell,
    td#{$block}__sticky-cell {
      text-align: start;
    }

    thead #{$block}__sticky-cell {
      background-color: var(--wb-color-shape-on-brand-default);
    }

    tbody tr:nth-child(odd) #{$block}__sticky-cell {
      background-color: var(--wb-color-shape-weaker-default);
    }

    tbody tr:nth-child(even) #{$block}__sticky-cell {
      background-color: var(--wb-color-shape-on-brand-default);
    }
  }

  &__sticky-cell {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: min(40vw, 210px);
    overflow-wrap: break-word;
  }

  &__sticky-cell {
    position: sticky;
    left: 0;
    z-index: 1;
    min-width: min(40vw, 210px);
    overflow-wrap: break-word;
  }

  .v-has-scroll-x &__sticky-cell::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    transform: translateX(100%);
    bottom: 0;
    width: 4px;
    pointer-events: none;
    background:
      linear-gradient(to right, rgb(0 0 0 / 0.16) 0, transparent 65%),
      linear-gradient(to left, rgb(255 255 255 / 0.28) 0, transparent 55%);
  }

  &__actions {
    display: flex;
    justify-content: center;

    margin-bottom: var(--wb-spacing-static-medium);
  }

  &__footnote {
    margin-inline: var(--inline-margin);
    margin-bottom: var(--wb-spacing-static-medium);
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
</style>
